import React from 'react'
import {
  DateTime,
  ImageThumbnail,
  RPCDocs,
  UserName,
} from '@therms/react-modules'
import {formatAddress, pluralize} from '@therms/web-js'
import './index.css'
import classNames from 'classnames'
import {Timer} from '@src/components/Timer.tsx'
import {VisitStatusLabel} from '@src/modules/Patrol/components/VisitStatusLabel.tsx'
import {getCompletedVisitRunStatusVariant} from '@src/modules/Patrol/utils/getCompletedVisitRunStatusVariant.ts'
import dayjs from 'dayjs'
import {Icon, Tooltip} from '@therms/atalaya'
import {MdKeyboardDoubleArrowDown, MdNoteAlt} from 'react-icons/md'
import {FaExclamationTriangle} from 'react-icons/fa'
import Status = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.VisitsStatus

type VisitElement = RPCDocs.Patrol.GetPatrolRouteRunDetails.v2.Visit

interface VisitRunProps {
  isDisabled?: boolean
  isInteractive?: boolean
  isMultiUserRun?: boolean
  onVisitRunSelect?: (visitRun: VisitElement) => void
  orderNumber: number
  runVisit: VisitElement
}

function VisitRun({
  isDisabled,
  isInteractive,
  isMultiUserRun,
  onVisitRunSelect,
  orderNumber,
  runVisit,
}: VisitRunProps) {
  const isRunActive = Boolean(isInteractive && onVisitRunSelect)
  const {address, bolos, name, passDowns, primaryImageUrl} =
    runVisit.visit.location || {}
  const {arrivalTime, departureTime, status, userId, visit} = runVisit

  const isInProgress = status === Status.InProgress

  const statusVariant = getCompletedVisitRunStatusVariant({
    arrivalTime,
    departureTime,
    lengthMin: visit.lengthMin,
    status,
  })

  const visitDuration = dayjs(departureTime || undefined).diff(
    arrivalTime,
    'minutes',
  )

  const shortAddress = formatAddress({
    ...address,
    postalCode: undefined,
    region: undefined,
  })

  return (
    <div
      className={classNames(
        {
          'cursor-pointer transition-colors': isRunActive && !isDisabled,
          'hover:bg-main-faded focus:bg-main-faded active:bg-main-dark':
            isRunActive && !isDisabled && status !== Status.Completed,
          'hover:bg-surface-strong focus:bg-surface-trong active:bg-surface-subtle':
            isRunActive && !isDisabled && status === Status.Completed,
          'opacity-50': isDisabled,
        },
        'bg-surface-subtle relative flex rounded',
      )}
      onClick={
        !isInteractive || isDisabled || !onVisitRunSelect
          ? undefined
          : () => onVisitRunSelect(runVisit)
      }
    >
      {isInProgress && (
        <div className="from-main-faded absolute inset-0 animate-pulse rounded bg-linear-to-l via-transparent" />
      )}

      <div
        className={classNames(
          {
            'border-border': !statusVariant && !isInProgress,
            'border-caution': statusVariant === 'caution',
            'border-main': isInProgress,
            'border-positive': statusVariant === 'positive',
          },
          'bg-surface pt-lg flex w-8 shrink-0 justify-center self-stretch rounded-l border-l-4 font-bold',
        )}
      >
        {orderNumber}
      </div>

      <div className="p-xs grow">
        <div className="flex flex-col sm:flex-row">
          <div className="gap-x-sm relative flex grow">
            {!!primaryImageUrl && (
              <div className="shrink-0">
                <ImageThumbnail src={primaryImageUrl} />
              </div>
            )}

            <div>
              <span>{name}</span>

              {!!shortAddress && (
                <span className="text-color-subtle text-sm">
                  {' '}
                  {shortAddress}
                </span>
              )}

              <div className="flex">
                {status === Status.Completed && departureTime ? (
                  <span className="tracking-wide">
                    <span
                      className={classNames({
                        'text-color-caution': statusVariant === 'caution',
                        'text-color-positive': statusVariant === 'positive',
                        'text-color-strong': !statusVariant,
                      })}
                    >
                      {visitDuration || 0}
                      {visit.lengthMin && `/${visit.lengthMin}`}
                    </span>
                    {pluralize(' Minute', visitDuration)}
                  </span>
                ) : (
                  <span>
                    <span className="text-color-strong">
                      {visit.lengthMin || 0}
                    </span>
                    <span> Minutes</span>
                    <span className="text-color-subtle text-xs"> Minimum</span>
                  </span>
                )}

                {isInteractive && (
                  <div className="gap-x-xxs ml-xs flex items-center">
                    {visit.notes && (
                      <Tooltip tooltip="Contains Notes">
                        <Icon Icon={MdNoteAlt} size="xs" color="info" />
                      </Tooltip>
                    )}

                    {!!bolos?.count && (
                      <Tooltip
                        tooltip={`${bolos.count} ${pluralize('Bolo', bolos.count)}`}
                      >
                        <span style={{color: bolos.color}}>
                          <Icon size="xs" Icon={FaExclamationTriangle} />
                        </span>
                      </Tooltip>
                    )}

                    {!!passDowns && (
                      <Tooltip
                        tooltip={`${passDowns} ${pluralize('Pass Down', passDowns)}`}
                      >
                        <Icon size="xs" Icon={MdKeyboardDoubleArrowDown} />
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="gap-x-xs mt-xs flex flex-wrap items-end sm:mt-0 sm:flex-col">
            <div className="gap-xs flex flex-wrap items-baseline">
              {isMultiUserRun && !!userId && (
                <span className="text-color-subtle text-sm">
                  <UserName userId={userId} />
                </span>
              )}

              {(!!arrivalTime || departureTime) && (
                <span className="">
                  {!!arrivalTime && (
                    <span className="text-color-subtle text-sm">
                      {!departureTime && 'Arrived '}

                      <DateTime
                        mode="time"
                        timestamp={arrivalTime}
                        toggleRelativeOnClick={false}
                      />
                    </span>
                  )}

                  {!!arrivalTime && !!departureTime && (
                    <span className="text-color-subtle"> - </span>
                  )}

                  {!!departureTime && (
                    <span className="text-color-subtle text-sm">
                      {!arrivalTime && 'Departed '}

                      <DateTime
                        mode="time"
                        timestamp={departureTime}
                        toggleRelativeOnClick={false}
                      />
                    </span>
                  )}
                </span>
              )}

              {isRunActive && isInProgress && !!arrivalTime && (
                <Timer
                  className="text-color-strong px-xxs bg-info-faded rounded text-center font-mono text-sm"
                  timestampStart={arrivalTime}
                  timestampEnd={departureTime}
                />
              )}
            </div>

            <VisitStatusLabel
              className={classNames(
                {
                  'text-color-caution': statusVariant === 'caution',
                  'text-color-positive': statusVariant === 'positive',
                },
                'ml-auto font-bold',
              )}
              status={status}
              isRunActive={isRunActive}
            />
          </div>
        </div>

        {!isInteractive && (runVisit.notes || visit.notes) && (
          <div className="mt-base space-y-xs">
            {runVisit.notes && (
              <div className="text-sm leading-relaxed">
                <span className="text-color-strong font-bold">Run Notes</span>{' '}
                <span>{runVisit.notes}</span>
              </div>
            )}

            {visit.notes && (
              <div className="text-sm leading-relaxed">
                <span className="font-bold">Notes</span>{' '}
                <span className="text-color-subtle">{visit.notes}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export {VisitRun}
